import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import type {
	ColDef,
	GridApi,
	GridReadyEvent,
	SelectionChangedEvent,
	FirstDataRenderedEvent,
} from "ag-grid-community";
import { TextField } from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { ErrorModal } from "@dexteel/mesf-core";
import LazyLoading from "@/components/loaders/LazyLoading/LazyLoading";
import type { Drawing } from "../../../../models/WorkOrderDrawing";

interface Props {
	allDrawings: Drawing[];
	workOrderDrawings: Drawing[];
	onSelectionChanged: (selectedDrawings: Drawing[]) => void;
	isLoading: boolean;
}

export const DrawingTable: React.FC<Props> = ({
	allDrawings,
	workOrderDrawings,
	onSelectionChanged,
	isLoading,
}) => {
	const [filterInput, setFilterInput] = useState("");
	const [error, setError] = useState<string>("");
	const [gridApi, setGridApi] = useState<GridApi | null>(null);

	const handlerFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
		setFilterInput(e.target.value);
	};

	const sortRows = () => {
		gridApi?.applyColumnState({
			state: [{ colId: "DocumentName", sort: "asc", sortIndex: 0 }],
		});
	};

	const loadingOverlayComponent = useMemo(() => LazyLoading, []);

	const rows = useMemo(() => {
		return allDrawings;
	}, [allDrawings]);

	const columns: ColDef[] = [
		{
			headerName: "",
			flex: 1,
			maxWidth: 33,
			headerCheckboxSelection: true,
			checkboxSelection: true,
			sortIndex: 1,
		},
		{
			field: "DocumentName",
			headerName: "Document",
			minWidth: 120,
			maxWidth: 120,
			flex: 2,
			sortIndex: 1,
			comparator: (a: any, b: any, nodeA, nodeB, isInverted) => {
				if (nodeA.isSelected() && !nodeB.isSelected()) {
					return -1;
				} else if (!nodeA.isSelected() && nodeB.isSelected()) {
					return 1;
				} else {
					return nodeA.data.DocumentName?.localeCompare(
						nodeB.data.DocumentName,
					);
				}
			},
		},
		{
			field: "Title",
			headerName: "Title",
			flex: 4,
			sortIndex: 1,
			comparator: (a: any, b: any, nodeA, nodeB, isInverted) => {
				if (nodeA.isSelected() && !nodeB.isSelected()) {
					return -1;
				} else if (!nodeA.isSelected() && nodeB.isSelected()) {
					return 1;
				} else {
					return nodeA.data.Title?.localeCompare(nodeB.data.Title);
				}
			},
		},
		{
			field: "VendorName",
			headerName: "Vendor Name",
			minWidth: 140,
			maxWidth: 140,
			flex: 3,
			sortIndex: 1,
			comparator: (a: any, b: any, nodeA, nodeB, isInverted) => {
				if (nodeA.isSelected() && !nodeB.isSelected()) {
					return -1;
				} else if (!nodeA.isSelected() && nodeB.isSelected()) {
					return 1;
				} else {
					return nodeA.data.VendorName?.localeCompare(nodeB.data.VendorName);
				}
			},
		},
	];

	const onAgGridSelectionChanged = (event: SelectionChangedEvent) => {
		sortRows();
		const selectedRows = event.api.getSelectedRows();
		onSelectionChanged(selectedRows);
	};

	const onFirstDataRendered = useCallback(
		(params: FirstDataRenderedEvent) => {
			if (workOrderDrawings && workOrderDrawings.length > 0) {
				params.api.forEachNode((node) => {
					const isSelected = workOrderDrawings.some(
						(drawing) => drawing.DocumentName === node.data.DocumentName,
					);
					if (isSelected) {
						node.setSelected(true);
					}
				});
			}
			sortRows();
		},
		[workOrderDrawings],
	);

	useEffect(() => {
		gridApi?.setGridOption("quickFilterText", filterInput.trim());
	}, [filterInput, gridApi]);

	const defaultColDef = useMemo<ColDef>(() => {
		return {
			sortable: true,
			wrapText: true,
			autoHeight: true,
			wrapHeaderText: true,
		};
	}, []);

	const onGridReady = (event: GridReadyEvent) => {
		setGridApi(event.api);
	};

	return (
		<>
			<Grid2
				container
				style={{ padding: "0 0 10px", justifyContent: "space-between" }}
			>
				<Grid2 size={{ xs: 12 }}>
					<TextField
						label="Search"
						variant="outlined"
						value={filterInput}
						onChange={handlerFilter}
						margin="dense"
						fullWidth
					/>
				</Grid2>
			</Grid2>
			<div style={{ width: "100%", height: "60vh" }}>
				<Grid2 style={{ width: "100%", height: "60vh" }}>
					<AgGridReact
						getRowId={(params) =>
							params.data.DocumentId?.toString() ||
							`${params.data.DocumentName}-${params.data.Title}`
						}
						rowHeight={30}
						headerHeight={25}
						animateRows={true}
						onGridReady={onGridReady}
						rowData={rows}
						columnDefs={columns}
						defaultColDef={defaultColDef}
						onFirstDataRendered={onFirstDataRendered}
						loadingOverlayComponent={loadingOverlayComponent}
						rowSelection="multiple"
						rowMultiSelectWithClick={true}
						onSelectionChanged={onAgGridSelectionChanged}
						suppressContextMenu={true}
						loading={isLoading}
					/>
				</Grid2>
			</div>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
